import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router';
import { useWallet } from '@solana/wallet-adapter-react';
import { ReplayEngine, type ReplayData, type ReplayState } from '../components/replay/ReplayEngine';
import ReplayCanvas from '../components/replay/ReplayCanvas';
import ReplayControls from '../components/replay/ReplayControls';
import { useReplayAudio } from '../hooks/useReplayAudio';
import ReplayPreview from '../components/replay/ReplayPreview';
import { WalletAvatar, WalletAvatarSmall } from '../components/ui/WalletAvatar';
import type { PhaseType } from '@beat-games/game-config';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface TopReplaysResponse {
  replays: ReplayData[];
  phaseType: PhaseType;
}

const PHASES: { id: PhaseType; label: string; short: string; color: string }[] = [
  { id: 'QUALIFIER', label: 'Qualifiers', short: 'QUA', color: '#22c55e' },
  { id: 'SHORTLIST', label: 'Shortlist', short: 'SHT', color: '#3b82f6' },
  { id: 'SEMIFINAL', label: 'Semifinals', short: 'SEM', color: '#a855f7' },
  { id: 'FINAL', label: 'Finals', short: 'FIN', color: '#fbbf24' },
];

const CATEGORIES = [
  { id: 'top', label: 'Top Scores', icon: '🏆' },
  { id: 'perfect', label: 'Perfect Runs', icon: '✨' },
  { id: 'mine', label: 'Your Replays', icon: '👤' },
];

function formatWallet(address: string): string {
  if (address.length <= 11) return address;
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

function formatScore(score: number): string {
  return (score / 1000).toFixed(3) + 's';
}

function formatDate(dateString: string): string {
  // SQLite stores UTC without 'Z' suffix, so append it for correct parsing
  const utcString = dateString.endsWith('Z') ? dateString : dateString.replace(' ', 'T') + 'Z';
  const date = new Date(utcString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

function getRankColor(rank: number): string {
  if (rank === 1) return '#fbbf24';
  if (rank === 2) return '#9ca3af';
  if (rank === 3) return '#cd7f32';
  return '#22c55e';
}

function getPhaseLabel(phaseType: PhaseType): string {
  switch (phaseType) {
    case 'QUALIFIER': return 'Qualifier';
    case 'SHORTLIST': return 'Shortlist';
    case 'SEMIFINAL': return 'Semifinal';
    case 'FINAL': return 'Final';
    default: return phaseType;
  }
}

const SPEEDS = [0.5, 1, 2] as const;

export default function Archive() {
  const { publicKey } = useWallet();
  const [activePhase, setActivePhase] = useState<PhaseType>('QUALIFIER');
  const [activeCategory, setActiveCategory] = useState('top');
  const [replays, setReplays] = useState<ReplayData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReplay, setSelectedReplay] = useState<ReplayData | null>(null);
  const [replayState, setReplayState] = useState<ReplayState | null>(null);
  const [isReplayAudioMuted, setIsReplayAudioMuted] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  const engineRef = useRef<ReplayEngine | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);

  // Initialize replay audio
  const { initializeAudio } = useReplayAudio({
    state: replayState,
    isMuted: isReplayAudioMuted,
  });

  const handleToggleMute = useCallback(async () => {
    await initializeAudio();
    setIsReplayAudioMuted((prev) => !prev);
  }, [initializeAudio]);

  const handleShareOnX = useCallback(() => {
    if (!selectedReplay) return;
    const scoreText = formatScore(selectedReplay.score);
    const isPerfect = selectedReplay.score >= 45000;
    const phaseLabel = getPhaseLabel(selectedReplay.phaseType);
    const text = isPerfect
      ? `🏆 A PERFECT ${scoreText} run on Hold Steady!\n\nSeason 1 ${phaseLabel}\n\nWatch this flawless performance:\nhttps://beatgames.fun/replay/${selectedReplay.sessionId}`
      : `🎯 ${scoreText} of pure precision on Hold Steady!\n\nSeason 1 ${phaseLabel}\n\nWatch the replay:\nhttps://beatgames.fun/replay/${selectedReplay.sessionId}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [selectedReplay]);

  const handleCopyLink = useCallback(async () => {
    if (!selectedReplay) return;
    const url = `${window.location.origin}/replay/${selectedReplay.sessionId}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, [selectedReplay]);

  // Initialize replay engine
  useEffect(() => {
    const engine = new ReplayEngine();
    engine.setOnStateChange(setReplayState);
    engineRef.current = engine;
    return () => engine.destroy();
  }, []);

  // Fetch replays when phase or category changes
  useEffect(() => {
    async function fetchReplays() {
      setIsLoading(true);
      setError(null);

      try {
        let url = `${API_BASE}/api/replay/top/${activePhase}`;
        if (activeCategory === 'mine' && publicKey) {
          url = `${API_BASE}/api/replay/wallet/${publicKey.toBase58()}?phase=${activePhase}`;
        } else if (activeCategory === 'perfect') {
          url = `${API_BASE}/api/replay/top/${activePhase}?perfect=true`;
        }

        const response = await fetch(url);
        const data = await response.json();

        if (data.success) {
          const responseData = data.data as TopReplaysResponse;
          setReplays(responseData.replays || []);
        } else {
          setError(data.error?.message || 'Failed to load replays');
          setReplays([]);
        }
      } catch {
        setError('Failed to load replays');
        setReplays([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchReplays();
  }, [activePhase, activeCategory, publicKey]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (!selectedReplay) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          engineRef.current?.togglePlayPause();
          break;
        case 'r':
        case 'R':
          engineRef.current?.restart();
          engineRef.current?.play();
          break;
        case '1':
          engineRef.current?.setSpeed(SPEEDS[0]);
          break;
        case '2':
          engineRef.current?.setSpeed(SPEEDS[1]);
          break;
        case '3':
          engineRef.current?.setSpeed(SPEEDS[2]);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (replayState) {
            const newElapsed = Math.max(0, replayState.elapsed - 1000);
            engineRef.current?.seek(newElapsed);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (replayState) {
            const newElapsed = Math.min(replayState.duration, replayState.elapsed + 1000);
            engineRef.current?.seek(newElapsed);
          }
          break;
        case 'm':
        case 'M':
          handleToggleMute();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedReplay, replayState, handleToggleMute]);

  // Load replay when selected, pause when closed
  useEffect(() => {
    if (selectedReplay && engineRef.current) {
      engineRef.current.load(selectedReplay);
      requestAnimationFrame(() => {
        engineRef.current?.play();
      });
    } else if (!selectedReplay && engineRef.current) {
      // Replay closed - pause the engine
      engineRef.current.pause();
    }
  }, [selectedReplay]);

  const handleSelectReplay = useCallback((replay: ReplayData) => {
    setSelectedReplay(replay);
    // Scroll to viewer
    setTimeout(() => {
      viewerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  const handlePlayPause = useCallback(() => {
    engineRef.current?.togglePlayPause();
  }, []);

  const handleSeek = useCallback((percent: number) => {
    engineRef.current?.seekPercent(percent);
  }, []);

  const handleSpeedChange = useCallback((speed: number) => {
    engineRef.current?.setSpeed(speed);
  }, []);

  const handleRestart = useCallback(() => {
    engineRef.current?.restart();
    engineRef.current?.play();
  }, []);

  const activePhaseConfig = PHASES.find((p) => p.id === activePhase);

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(to right, white 1px, transparent 1px),
              linear-gradient(to bottom, white 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        {/* Radial spotlight */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255,255,255,0.02) 0%, transparent 60%)',
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="pt-12 pb-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-6 animate-slide-up-fade">
              <div className="h-px flex-1 max-w-[100px] bg-gradient-to-r from-transparent to-white/20" />
              <span className="font-mono text-[10px] tracking-[0.4em] text-white/30">REPLAY THEATER</span>
              <div className="h-px flex-1 max-w-[100px] bg-gradient-to-l from-transparent to-white/20" />
            </div>

            <h1 className="font-display text-[clamp(3rem,10vw,7rem)] leading-[0.9] tracking-[0.02em] text-center mb-4 animate-slide-up-fade" style={{ animationDelay: '0.05s' }}>
              <span className="text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/30">
                THE ARCHIVE
              </span>
            </h1>

            <p className="text-center font-mono text-sm text-white/40 max-w-lg mx-auto animate-slide-up-fade" style={{ animationDelay: '0.1s' }}>
              Relive the most legendary runs. Study the masters. Learn their techniques.
            </p>
          </div>
        </header>

        {/* Filters */}
        <div className="border-y border-white/[0.06] bg-white/[0.01]">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Phase filter */}
              <div className="flex flex-wrap justify-center gap-1 sm:gap-0 sm:flex-nowrap bg-white/[0.02] border border-white/[0.06] p-1.5 sm:p-1 animate-slide-up-fade" style={{ animationDelay: '0.15s' }}>
                {PHASES.map((phase) => (
                  <button
                    key={phase.id}
                    onClick={() => setActivePhase(phase.id)}
                    className={`min-w-[44px] min-h-[44px] px-4 py-3 font-mono text-xs tracking-wider transition-all ${activePhase === phase.id ? 'bg-white text-black' : 'text-white/40 hover:text-white/70'
                      }`}
                  >
                    <span className="hidden sm:inline">{phase.label}</span>
                    <span className="sm:hidden">{phase.short}</span>
                  </button>
                ))}
              </div>

              {/* Category filter */}
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 animate-slide-up-fade" style={{ animationDelay: '0.2s' }}>
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    disabled={cat.id === 'mine' && !publicKey}
                    className={`flex items-center gap-2 min-h-[44px] px-3 sm:px-4 py-3 font-mono text-xs tracking-wider transition-all border ${activeCategory === cat.id
                      ? 'border-white/20 bg-white/10 text-white'
                      : 'border-white/[0.06] text-white/40 hover:text-white/70 hover:border-white/10'
                      } ${cat.id === 'mine' && !publicKey ? 'opacity-30 cursor-not-allowed' : ''}`}
                  >
                    <span>{cat.icon}</span>
                    <span className="hidden sm:inline">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Inline Replay Viewer */}
          {selectedReplay && (
            <section ref={viewerRef} className="mb-12 animate-slide-up-fade">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-[10px] text-white/30 tracking-[0.2em]">NOW PLAYING</span>
                <div className="flex-1 h-px bg-white/10" />
                <button
                  onClick={() => setSelectedReplay(null)}
                  className="flex items-center gap-1.5 text-white/40 hover:text-white/70 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="font-mono text-[10px] tracking-wider">CLOSE</span>
                </button>
              </div>

              <div className="border border-white/10 bg-white/[0.02] overflow-hidden">
                <div className="flex flex-col lg:flex-row">
                  {/* Canvas area */}
                  <div className="flex-1 p-4 lg:p-6">
                    <div className="relative bg-black/30 rounded-lg overflow-hidden border border-white/5">
                      <ReplayCanvas
                        state={replayState}
                        walletAddress={selectedReplay.walletAddress}
                        phaseType={selectedReplay.phaseType}
                      />
                    </div>

                    {/* Controls */}
                    <div className="mt-4">
                      <ReplayControls
                        state={replayState}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        onSpeedChange={handleSpeedChange}
                        onRestart={handleRestart}
                        disabled={false}
                        isMuted={isReplayAudioMuted}
                        onToggleMute={handleToggleMute}
                      />
                    </div>

                    {/* Keyboard shortcuts */}
                    <div className="flex items-center justify-center gap-6 mt-4 text-white/20 text-xs">
                      <div className="flex items-center gap-1.5">
                        <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded font-mono text-[10px]">Space</kbd>
                        <span>Play/Pause</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded font-mono text-[10px]">R</kbd>
                        <span>Restart</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded font-mono text-[10px]">M</kbd>
                        <span>Mute</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded font-mono text-[10px]">1-3</kbd>
                        <span>Speed</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded font-mono text-[10px]">←/→</kbd>
                        <span>Seek</span>
                      </div>
                    </div>
                  </div>

                  {/* Info sidebar */}
                  <div className="w-full lg:w-72 p-4 lg:p-6 border-t lg:border-t-0 lg:border-l border-white/[0.06]">
                    <div className="font-mono text-[10px] text-white/30 tracking-wider mb-2">PLAYER</div>
                    <Link
                      to={`/profile/${selectedReplay.walletAddress}`}
                      className="flex items-center gap-3 mb-4 group"
                    >
                      <WalletAvatar address={selectedReplay.walletAddress} size={40} />
                      <span className="font-mono text-sm text-white group-hover:text-white/80 transition-colors">
                        {formatWallet(selectedReplay.walletAddress)}
                      </span>
                    </Link>

                    <div className="space-y-4">
                      <div>
                        <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">FINAL TIME</div>
                        <div className="font-mono text-2xl text-[#fbbf24]">{formatScore(selectedReplay.score)}</div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">PHASE</div>
                        <span
                          className="inline-block px-2 py-1 font-mono text-xs tracking-wider"
                          style={{
                            backgroundColor: `${activePhaseConfig?.color}20`,
                            color: activePhaseConfig?.color,
                          }}
                        >
                          {selectedReplay.phaseType}
                        </span>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">DATE</div>
                        <div className="font-mono text-sm text-white/60">{formatDate(selectedReplay.startedAt)}</div>
                      </div>
                    </div>

                    {selectedReplay.score >= 45000 && (
                      <div className="mt-6 p-3 border border-[#fbbf24]/20 bg-[#fbbf24]/5">
                        <div className="flex items-center gap-2">
                          <span className="text-[#fbbf24]">✨</span>
                          <span className="font-mono text-xs text-[#fbbf24]/80 tracking-wider">PERFECT RUN</span>
                        </div>
                      </div>
                    )}

                    {/* Share buttons */}
                    <div className="mt-6 space-y-2">
                      <div className="font-mono text-[10px] text-white/30 tracking-wider mb-2">SHARE</div>
                      <button
                        onClick={handleShareOnX}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all font-mono text-sm tracking-wider"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                        <span>Share on X</span>
                      </button>
                      <button
                        onClick={handleCopyLink}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all font-mono text-sm tracking-wider"
                      >
                        {linkCopied ? (
                          <>
                            <svg className="w-4 h-4 text-[#22c55e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-[#22c55e]">Copied!</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Copy Link</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Replay Grid */}
          <section className="animate-slide-up-fade" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[10px] text-white/30 tracking-[0.2em]">
                {activeCategory === 'top' ? 'TOP 10 RUNS' : activeCategory === 'perfect' ? 'PERFECT RUNS' : 'YOUR REPLAYS'}
              </span>
              <div className="flex-1 h-px bg-white/10" />
              <span className="font-mono text-[10px] text-white/20">{replays.length} replays</span>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="aspect-[4/3] bg-white/[0.02] border border-white/[0.06] animate-pulse"
                    style={{ animationDelay: `${i * 100}ms` }}
                  />
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-16 border border-white/[0.06] bg-white/[0.01]">
                <div className="w-12 h-12 mx-auto mb-4 border border-[#ef4444]/20 bg-[#ef4444]/5 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#ef4444]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="text-white/50 mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 border border-white/20 text-white/60 font-mono text-sm tracking-wider hover:border-white/40 hover:text-white transition-all"
                >
                  RETRY
                </button>
              </div>
            ) : replays.length === 0 ? (
              <div className="text-center py-16 border border-white/[0.06] bg-white/[0.01]">
                <div className="w-16 h-16 mx-auto mb-4 border border-white/10 bg-white/[0.02] rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-display text-xl mb-2">No Replays Available</h3>
                <p className="text-white/40 text-sm mb-6">
                  {activeCategory === 'mine'
                    ? "You haven't completed any games in this phase yet"
                    : 'Replays will appear after games are completed'}
                </p>
                <Link
                  to="/play"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-bold text-sm tracking-wider"
                >
                  <span>PLAY NOW</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {replays.map((replay, index) => {
                  const rank = index + 1;
                  const rankColor = getRankColor(rank);
                  const isSelected = selectedReplay?.sessionId === replay.sessionId;

                  return (
                    <button
                      key={replay.sessionId}
                      onClick={() => handleSelectReplay(replay)}
                      className={`group relative text-left border transition-all hover:border-white/20 ${isSelected
                        ? 'border-white/30 bg-white/[0.05]'
                        : 'border-white/[0.06] bg-white/[0.01] hover:bg-white/[0.03]'
                        }`}
                    >
                      {/* Preview area */}
                      <div className="relative aspect-[4/3] bg-black/20 overflow-hidden">
                        {/* Final state preview */}
                        <ReplayPreview
                          replay={replay}
                          phaseColor={activePhaseConfig?.color || '#ffffff'}
                        />

                        {/* Play overlay */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                            <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5.14v14.72a1 1 0 001.5.86l11-7.36a1 1 0 000-1.72l-11-7.36a1 1 0 00-1.5.86z" />
                            </svg>
                          </div>
                        </div>

                        {/* Rank badge */}
                        <div className="absolute top-3 left-3">
                          <div
                            className="w-8 h-8 rounded flex items-center justify-center font-display text-sm"
                            style={{
                              backgroundColor: `${rankColor}15`,
                              color: rankColor,
                            }}
                          >
                            {rank}
                          </div>
                        </div>

                        {/* Perfect badge */}
                        {replay.score >= 45000 && (
                          <div className="absolute top-3 right-3">
                            <span className="px-2 py-1 bg-[#fbbf24]/20 text-[#fbbf24] font-mono text-[9px] tracking-wider">
                              PERFECT
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <WalletAvatarSmall address={replay.walletAddress} />
                            <span className="font-mono text-sm text-white/80">
                              {formatWallet(replay.walletAddress)}
                            </span>
                          </div>
                          <span
                            className="font-mono text-sm font-semibold"
                            style={{ color: rank <= 3 ? rankColor : '#22c55e' }}
                          >
                            {formatScore(replay.score)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[10px] text-white/30">
                            {formatDate(replay.startedAt)}
                          </span>
                          <span
                            className="font-mono text-[9px] tracking-wider px-1.5 py-0.5"
                            style={{
                              backgroundColor: `${activePhaseConfig?.color}15`,
                              color: `${activePhaseConfig?.color}99`,
                            }}
                          >
                            {replay.phaseType}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* CTA */}
          <section className="mt-16 text-center border-t border-white/[0.06] pt-12">
            <p className="font-mono text-sm text-white/30 mb-4">Think you can do better?</p>
            <Link
              to="/play"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-bold tracking-wider hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all"
            >
              <span>ENTER THE ARENA</span>
              <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
}
